import React, {useState} from 'react';
import {Button, Checkbox, Input, Modal, Tag, Typography} from "antd";

const {Paragraph, Text} = Typography;

const DEFAULT_RULES_DOCUMENT = '/2027_Stallion_Enrollment_Rules_.pdf';
const RULES_VERSION = '2027 Stallion Enrollment Rules';

const formatUSD = (n) => `$${Number(n).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;

/**
 * Two-step signature used on the payment page (rules popup -> typed-name popup).
 * Writes to the generic `signed*` fields on the payment form; the caller maps
 * them to the correct persisted fields (enrollment vs renewal) on submit.
 */
const SignatureGate = ({applicationType, stallionName, amountDueValue, baseFeeValue, values, setFieldValue, onSignatureSaved, rulesDocumentUrl}) => {
  const isRenewal = applicationType === 'Renewal';
  const [rulesModalOpen, setRulesModalOpen] = useState(false);
  const [signModalOpen, setSignModalOpen] = useState(false);
  const [rulesOpened, setRulesOpened] = useState(false);
  const [agreed, setAgreed] = useState(Boolean(values?.signed));
  const [signerName, setSignerName] = useState(values?.signedName || '');

  const amountDue = formatUSD(amountDueValue);
  const baseFeeText = formatUSD(baseFeeValue);
  const name = stallionName || 'this stallion';
  const verb = isRenewal ? 'renewing' : 'enrolling';
  const withWhat = isRenewal ? 'renewal' : 'enrollment';
  const feeLabel = isRenewal ? 'renewal fee' : 'enrollment fee';
  const modalTitle = isRenewal ? 'Renewal Signature' : 'Enrollment Signature';

  const legalText = `I have read and accept the Tomorrow’s Legends Enrollment Rules. I understand and agree that by signing this I am ${verb} “${name}” in Tomorrow’s Legends for one year. I understand and agree that payment in the amount of ${amountDue} is required with ${withWhat}. By signing this I agree to pay the ${feeLabel} of ${baseFeeText}. I understand failure to pay will result in late fees applied in the amount of $100 per month. Overdue accounts will be subject to collection action resulting in further collection costs and reasonable attorney’s fees.`;

  const canSign = agreed && signerName.trim().length > 0;

  const openRulesFlow = () => setRulesModalOpen(true);
  const agreeToRules = () => {
    setRulesModalOpen(false);
    setSignModalOpen(true);
  };

  const confirmSignature = async () => {
    if (!canSign) return;
    const signatureFields = {
      signed: true,
      signedAt: new Date().toISOString(),
      signedName: signerName.trim(),
      signedStatement: legalText,
      signedRulesVersion: RULES_VERSION,
    };
    if (onSignatureSaved) {
      try {
        await onSignatureSaved(signatureFields);
      } catch {
        return;
      }
    }
    setFieldValue('signed', signatureFields.signed);
    setFieldValue('signedAt', signatureFields.signedAt);
    setFieldValue('signedName', signatureFields.signedName);
    setFieldValue('signedStatement', signatureFields.signedStatement);
    setFieldValue('signedRulesVersion', signatureFields.signedRulesVersion);
    setSignModalOpen(false);
  };

  const clearSignature = () => {
    setAgreed(false);
    setRulesOpened(false);
    setSignerName('');
    setFieldValue('signed', false);
    setFieldValue('signedAt', null);
    setFieldValue('signedName', '');
    setFieldValue('signedStatement', '');
    setFieldValue('signedRulesVersion', '');
  };

  return (
    <div style={{marginBottom: '16px'}}>
      {values?.signed ? (
        <div>
          <Text type="success" strong>
            ✓ Rules read and signed
            {values?.signedName ? ` by ${values.signedName}` : ''}
            {values?.signedAt ? ` on ${new Date(values.signedAt).toLocaleString()}` : ''}.
          </Text>
          <div style={{marginTop: '8px'}}>
            <Button size="small" onClick={() => { clearSignature(); openRulesFlow(); }}>
              Review / re-sign
            </Button>
          </div>
        </div>
      ) : (
        <div>
          <Button type="primary" onClick={openRulesFlow}>
            Read Rules &amp; Sign
          </Button>
          <Tag style={{marginLeft: '8px'}} color="red">
            *Required before payment
          </Tag>
        </div>
      )}

      <Modal
        title="Tomorrow’s Legends Enrollment Rules"
        open={rulesModalOpen}
        onCancel={() => setRulesModalOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setRulesModalOpen(false)}>
            Cancel
          </Button>,
          <Button key="agree" type="primary" disabled={!rulesOpened} onClick={agreeToRules}>
            I have opened and agree to the Rules
          </Button>,
        ]}
      >
        <Paragraph>
          Please open and read the Tomorrow’s Legends Enrollment Rules, then agree to continue to signing.
        </Paragraph>
        <Paragraph>
          <a
            href={rulesDocumentUrl || DEFAULT_RULES_DOCUMENT}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setRulesOpened(true)}
          >
            Open the Tomorrow’s Legends Enrollment Rules (PDF)
          </a>
        </Paragraph>
        {!rulesOpened && (
          <Text type="secondary">
            You must open the rules document to enable the “I agree” button.
          </Text>
        )}
      </Modal>

      <Modal
        title={modalTitle}
        open={signModalOpen}
        onCancel={() => setSignModalOpen(false)}
        footer={[
          <Button key="back" onClick={() => { setSignModalOpen(false); setRulesModalOpen(true); }}>
            Back
          </Button>,
          <Button key="sign" type="primary" disabled={!canSign} onClick={confirmSignature}>
            Sign &amp; Continue
          </Button>,
        ]}
      >
        <Checkbox
          checked={agreed}
          onChange={(e) => setAgreed(e.target.checked)}
          style={{alignItems: 'flex-start'}}
        >
          <span style={{display: 'inline-block'}}>{legalText}</span>
        </Checkbox>
        <div style={{marginTop: '16px'}}>
          <Text strong>Type your full legal name to sign:</Text>
          <Input
            style={{marginTop: '6px'}}
            placeholder="Full legal name"
            value={signerName}
            onChange={(e) => setSignerName(e.target.value)}
          />
        </div>
        <div style={{marginTop: '12px'}}>
          <Text type="secondary">
            {canSign
              ? `Signing as “${signerName.trim()}”. Your signature will be time-stamped ${new Date().toLocaleString()} when you continue.`
              : 'Check the box and type your full name to sign. Your signature will be time-stamped.'}
          </Text>
        </div>
      </Modal>
    </div>
  );
};

export default SignatureGate;
